'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '../../lib/supabase/client';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '../landing-page/components/LandingFooter';

interface UpdateEntry {
  label: string;
  date: string;
  headline: string;
  summary: string;
  slug: string;
}

const UPDATES: UpdateEntry[] = [
  {
    label: 'ANNA UPDATE',
    date: 'May 2026',
    headline: 'Everyone Is Talking About AI Agents. Here\'s What Actually Matters.',
    summary:
      'AI agents are getting significant coverage. Here\'s what\'s actually relevant for professionals over 50 — and what can safely be ignored.',
    slug: 'ai-agents',
  },
];

export default function UpdatesPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session || !session.user.email_confirmed_at) {
        router.push('/conversion-page');
        return;
      }
      setLoading(false);
    });
  }, [router]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F7F4' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#D4891A', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <SiteNav variant="white" currentPath="/updates" />

      <div className="max-w-screen-md mx-auto px-6 lg:px-8 py-16 lg:py-24">
        {/* Page header */}
        <div className="mb-12">
          <h1
            className="font-display text-4xl lg:text-5xl font-semibold mb-3 leading-tight"
            style={{ color: '#1B2A4A' }}
          >
            Anna&rsquo;s Updates
          </h1>
          <p className="font-body text-base" style={{ color: '#6B7280' }}>
            Occasional guidance on what matters in AI — and what doesn&rsquo;t.
          </p>
        </div>

        {/* Update entries */}
        <div className="space-y-6">
          {UPDATES.map((update) => (
            <div
              key={update.slug}
              className="bg-white rounded-xl p-7 lg:p-8"
              style={{ border: '1px solid rgba(0,0,0,0.08)' }}
            >
              <p
                className="font-body text-xs font-semibold uppercase tracking-widest mb-1"
                style={{ color: '#D4891A', letterSpacing: '0.14em' }}
              >
                {update.label}
              </p>
              <p className="font-body text-xs mb-3" style={{ color: '#9CA3AF' }}>
                {update.date}
              </p>
              <h2
                className="font-display text-xl font-semibold mb-3 leading-snug"
                style={{ color: '#1B2A4A' }}
              >
                {update.headline}
              </h2>
              <p
                className="font-body text-sm leading-relaxed mb-5"
                style={{ color: '#6B7280' }}
              >
                {update.summary}
              </p>
              <Link
                href={`/updates/${update.slug}`}
                className="font-body text-sm font-semibold transition-colors duration-150 hover:opacity-75"
                style={{ color: '#D4891A' }}
              >
                Read →
              </Link>
            </div>
          ))}
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
